import { useContext, useEffect, useState } from "react";
import { useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../../api/queryClient";
import AppContext from "../../../../AppContext";
import { APP_TITLE } from "../../../../config/constant";
import PopupContext from "../../../../PopupContext";
import * as Library from "../../../../utils/Library";
import BtnLargeSolid from "../../../common/buttons/BtnLargeSolid";
import DialogBoxComponent from "../../../common/dialogs/DialogBoxComponent";
import FormContainer from "../../../common/forms/FormContainer";
import FormField from "../../../common/forms/FormField";
import ListComponent, { StatusBadge } from "../../../common/lists/ListComponent";
import ContainerLoader from "../../../common/loaders/ContainerLoader";
import MainWindowHeader, { CustomSearch } from "../../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FullScreenOverlay from "../../../common/overlay/FullScreenOverlay";

// #region SCOPED CSS
const StyledPage = styled.div`
    &.my-defaults-categories-page {
        display: flex;
        flex-direction: column;
    }

    .add-category-row {
        display: flex;
        align-items: flex-end;
        gap: 16px;
        width: 100%;
    }

    .add-category-parent-name,
    .add-category-sub-name {
        flex: 0 0 280px;
        min-width: 0;
    }

    .add-category-size-system {
        flex: 0 0 280px;
        min-width: 0;
    }

    .add-category-variants {
        flex: 1 1 auto;
        min-width: 320px;
    }

    .add-category-button {
        width: 64px;
        height: 37px;
        flex-shrink: 0;
    }

    .table-action-icon {
        width: 16px;
        height: 16px;
        color: var(--text-icons-gray-100);
    }

    @media (max-width: 900px) {
        .add-category-row {
            flex-wrap: wrap;
        }

        .add-category-parent-name,
        .add-category-sub-name,
        .add-category-size-system,
        .add-category-variants {
            flex-basis: 100%;
        }
    }

    .rdt_Pagination {
        display: none !important;
    }

    .table-structure > div:nth-child(2) {
        height: 24px;
    }
`;

const SizeSystemTooltipContent = styled.div`
    max-height: 240px;
    overflow-y: auto;
    overflow-wrap: anywhere;
    padding-right: 4px;
    white-space: normal;
`;
// #endregion

export default function MyDefaultCategsPage() {
    document.title = `${APP_TITLE} - My Default Categories`;

    // #region VARS
    const { isLoadingApp, setHasUnsavedChanges } = useContext(AppContext);
    const { MainLayoutProps, layoutType } = useOutletContext();
    const { setGenericConfirmationPopup } = useContext(PopupContext);

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    // #endregion

    // #region STATES
    //----- Header Filters -----
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [allPOSList, setAllPOSList] = useState([]);
    const [selectedRetailBrand, setSelectedRetailBrand] = useState(null);
    const [filteredPOS, setFilteredPOS] = useState(undefined);
    const [requestsEnabled, setRequestsEnabled] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [sizeSystemDropdownData, setSizeSystemDropdownData] = useState([]);

    const filters = {
        filterRetailBrandIDs: selectedRetailBrand ? [selectedRetailBrand?.value] : [],
        filterPOSIDs: filteredPOS?.map(item => item?.value)
        //searchText: searchText
    };

    // #endregion

    // #region DATA MODEL STATES
    const [parentCategoryName, setParentCategoryName] = useState("");
    const [subCategoryName, setSubCategoryName] = useState("");
    const [selectedSizeSystem, setSelectedSizeSystem] = useState(null);
    const [editSaveButtonEnabled, setEditSaveButtonEnabled] = useState(false);
    const [editingCategoryDefault, setEditingCategoryDefault] = useState(null);
    const [categoryEditSnapshot, setCategoryEditSnapshot] = useState(null);
    // #endregion

    // #region LIST COMPONENT VARS
    const [listComponentIsLoading, setListComponentIsLoading] = useState(false);
    const [listNumRows, setListNumRows] = useState(50);
    const listQueryKey = ["MyDefaultCategoriesList", filters];
    const listColumns = [
        {
            name: "Parent category",
            selector: row => row?.parentCategoryName ?? "--"
        },
        {
            name: "Parent ID",
            selector: row => row?.parentCategoryID ?? "--"
        },
        {
            name: "Sub-category",
            selector: row => row?.subCategoryName || "--"
        },
        {
            name: "Category record ID",
            selector: row => row?.categoryRecordID ?? "--"
        },
        {
            name: "Size system",
            selector: row => row?.sizeSystemName || "--",
            tooltipContent: row => (
                <SizeSystemTooltipContent>
                    {row?.relatedSizeNames?.length ? row.relatedSizeNames.join(", ") : "--"}
                </SizeSystemTooltipContent>
            ),
            tooltipOverlayStyle: { maxWidth: "600px" }
        },
        {
            name: "Created by",
            selector: row => row?.createdBy || "--"
        },
        {
            name: "Created on",
            selector: row => row?.createdOn || "--"
        },
        {
            name: "Status",
            selector: row => <StatusBadge statusValue={row?.status ?? "--"} colorValue={"#00bb00"} />
        }
    ];

    const mapListData = data => {
        const mappedCategoryDefaults = data?.map(item => {
            return {
                parentCategoryName: item?.categoryNameFromPOS,
                parentCategoryID: "--",
                subCategoryName: item?.subCategoryNameFromPOS,
                categoryRecordID: item?.categoryIdFromPOS,
                sizeSystemName: item?.relatedSizeGroupNameFromPOS,
                createdBy: "--",
                createdOn: "--",
                completeCategoryNameFromPOS: item?.completeCategoryNameFromPOS,
                relatedSizeGroupIdFromPOS: item?.relatedSizeGroupIdFromPOS,
                relatedSizeNames: Array.isArray(item?.relatedSizeNames) ? item.relatedSizeNames : [],
                status: item?.status
            };
        });

        return mappedCategoryDefaults;
    };
    // #endregion

    // #region METHODS
    const onSearch = value => {
        setSearchValue(value || "");
    };

    const onRefreshAction = () => {
        AppQueryClient.invalidateQueries(listQueryKey);
    };

    const valueIsPresent = value => value !== undefined && value !== null && String(value).trim() !== "";

    const normalizeCategorySnapshotValue = value => (valueIsPresent(value) ? String(value) : "");

    const categorySnapshotsAreEqual = (snapshotA, snapshotB) => {
        if (!snapshotA || !snapshotB) return false;

        return Object.keys(snapshotA).every(key => snapshotA[key] === snapshotB[key]);
    };

    const getSizeSystemDropdownDataWithSelection = (dropdownData, selectedOption) => {
        const currentDropdownData = dropdownData || [];
        const selectedValue = selectedOption?.value;
        const selectedName = selectedOption?.name;
        let selectedOptionExists = false;

        const dropdownDataWithSelection = currentDropdownData.map(item => {
            const isSelected = valueIsPresent(selectedValue)
                ? normalizeCategorySnapshotValue(item?.value) === normalizeCategorySnapshotValue(selectedValue)
                : item?.name === selectedName;

            if (isSelected) selectedOptionExists = true;
            return { ...item, selected: isSelected };
        });

        if (!selectedOption || selectedOptionExists) return dropdownDataWithSelection;
        if (!valueIsPresent(selectedValue) && !valueIsPresent(selectedName)) return dropdownDataWithSelection;

        return [
            ...dropdownDataWithSelection,
            {
                value: selectedValue ?? "",
                name: selectedName || selectedValue || "--",
                selected: true
            }
        ];
    };

    const getSizeSystemOptionFromRow = row => {
        const sizeSystemValue = row?.relatedSizeGroupIdFromPOS;
        const sizeSystemName = row?.sizeSystemName;

        if (!valueIsPresent(sizeSystemValue) && !valueIsPresent(sizeSystemName)) return null;

        return {
            value: sizeSystemValue ?? "",
            name: sizeSystemName || sizeSystemValue || "--"
        };
    };

    const getCurrentCategorySnapshot = () => ({
        categoryNameFromPOS: parentCategoryName ?? "",
        subCategoryNameFromPOS: subCategoryName ?? "",
        relatedSizeGroupIdFromPOS: normalizeCategorySnapshotValue(selectedSizeSystem?.value),
        relatedSizeGroupNameFromPOS: selectedSizeSystem?.name ?? ""
    });

    const getCategorySnapshotFromRow = row => ({
        categoryNameFromPOS: row?.parentCategoryName ?? "",
        subCategoryNameFromPOS: row?.subCategoryName ?? "",
        relatedSizeGroupIdFromPOS: normalizeCategorySnapshotValue(row?.relatedSizeGroupIdFromPOS),
        relatedSizeGroupNameFromPOS: row?.sizeSystemName ?? ""
    });

    const categoryFormIsValid = () => {
        if (!valueIsPresent(parentCategoryName)) return false;
        return valueIsPresent(selectedSizeSystem?.value);
    };

    const resetCategoryForm = () => {
        setParentCategoryName("");
        setSubCategoryName("");
        setSelectedSizeSystem(null);
        setSizeSystemDropdownData(prev => getSizeSystemDropdownDataWithSelection(prev, null));
        setEditSaveButtonEnabled(false);
        setEditingCategoryDefault(null);
        setCategoryEditSnapshot(null);
        setHasUnsavedChanges(false);
    };

    const getCategoryDefaultPayload = () => ({
        retailBrandID: selectedRetailBrand?.value,
        categoryNameFromPOS: parentCategoryName,
        subCategoryNameFromPOS: subCategoryName,
        relatedSizeGroupIdFromPOS: selectedSizeSystem?.value ?? "",
        relatedSizeGroupNameFromPOS: selectedSizeSystem?.name ?? ""
    });

    const onFiltersLoaded = data => {
        const defaultRetailBrandValue =
            sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map(item => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }

                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: item?._id === defaultRetailBrandValue
                };
            });

            const defaultRetailBrandObj =
                retailBrands?.find(item => item?.value === defaultRetailBrandValue) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setSelectedRetailBrand(defaultRetailBrandObj);
        }

        if (data?.posSystems?.length) {
            const formFields = data?.posSystems?.map(item => ({
                ...item,
                name: item.name,
                value: item._id,
                selected: true
            }));

            setAllPOSList(formFields);
            setFilteredPOS(formFields);
        }

        setRequestsEnabled(true);
    };

    const onRetailBrandChange = selection => {
        resetCategoryForm();
        setSelectedRetailBrand(selection);
        const currentRetailBrandID = selection?.value;
        sessionStorage.setItem("currentRetailBrand", currentRetailBrandID || "");
    };

    const onSelectedPOSChanged = (selections, dropdownDataWithSelections) => {
        resetCategoryForm();
        setFilteredPOS(selections);
        setAllPOSList(dropdownDataWithSelections);
    };

    const onSizeSystemChange = selection => {
        setSelectedSizeSystem(selection);
        setSizeSystemDropdownData(prev => getSizeSystemDropdownDataWithSelection(prev, selection));
    };

    const onClickEditHandler = row => {
        if (!valueIsPresent(row?.categoryRecordID)) {
            Library.showToastMessage({
                type: "warning",
                message: "Unable to edit this category because the POS category record ID is missing."
            });
            return;
        }

        const rowSnapshot = getCategorySnapshotFromRow(row);
        const rowSizeSystem = getSizeSystemOptionFromRow(row);

        setEditingCategoryDefault({
            categoryIdFromPOS: row.categoryRecordID,
            categoryNameFromPOS: row?.parentCategoryName
        });
        setCategoryEditSnapshot(rowSnapshot);
        setParentCategoryName(rowSnapshot.categoryNameFromPOS);
        setSubCategoryName(rowSnapshot.subCategoryNameFromPOS);
        setSelectedSizeSystem(rowSizeSystem);
        setSizeSystemDropdownData(prev => getSizeSystemDropdownDataWithSelection(prev, rowSizeSystem));
        setEditSaveButtonEnabled(false);
        setHasUnsavedChanges(false);
    };

    const saveCategoryEditClicked = async () => {
        if (!editingCategoryDefault || !editSaveButtonEnabled) return;

        const success = await updateCategoryDefaultRequest(editingCategoryDefault.categoryIdFromPOS);
        if (!success) return;

        Library.showToastMessage({
            type: "success",
            title: "Category updated!",
            message: "Updates are saved in BOtag. This update is not exported to POS."
        });

        resetCategoryForm();
    };
    // #endregion

    // #region REQUESTS
    const getProductSizeGroupsFromPOSRequest = async () => {
        if (!selectedRetailBrand?.value) {
            setSizeSystemDropdownData([]);
            return;
        }

        const res = await Library.makeApiRequest({
            endpoint: "X/Products/getProductSizeGroupsFromPOS",
            method: "POST",
            data: {
                retailBrandID: selectedRetailBrand.value
            }
        });

        if (res?.data?.status !== 200) return;

        const sizeGroups = res?.data?.data || [];
        const dropdownData = sizeGroups?.map(item => ({
            value: item?.sizeGroupID,
            name: item?.sizeGroupName
        }));

        setSizeSystemDropdownData(getSizeSystemDropdownDataWithSelection(dropdownData, selectedSizeSystem));
    };

    // Dormant for this phase: create/add must stay disabled until the create workflow is enabled.
    /* const createCategoryDefaultRequest = async () => {
        const res = await Library.makeApiRequest({
            endpoint: "createCategoryDefault",
            method: "POST",
            data: getCategoryDefaultPayload(),
            setLoader: setIsSaving
        });

        if (res?.data?.status !== 200) return false;

        onRefreshAction();
        resetCategoryForm();
        return true;
    }; */

    const updateCategoryDefaultRequest = async categoryIdFromPOS => {
        const res = await Library.makeApiRequest({
            endpoint: "updateCategoryDefault",
            method: "POST",
            data: {
                ...getCategoryDefaultPayload(),
                categoryIdFromPOS
            },
            setLoader: setIsSaving
        });

        if (res?.data?.status !== 200) return false;

        onRefreshAction();
        return true;
    };

    const onUnlinkClick = async item => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            headingText: "Just Checking...",
            headingColor: "var(--text-icons-orange-500)",
            title: "Unlink size system?",
            messageHtml: "This will remove the size system you have linked to this Category.",
            confirmText: "UNLINK",
            cancelText: "CANCEL",
            confirmState: "secondary",
            onConfirmClicked: () => {
                const categoryIdFromPOS = item?.categoryRecordID;
                if (!categoryIdFromPOS) return false;

                return unlinkCategoryDefaultRequest(categoryIdFromPOS);
            },
            onCancelClicked: () => {
                setGenericConfirmationPopup(false);
            }
        });
    };

    const unlinkCategoryDefaultRequest = async categoryIdFromPOS => {
        const res = await Library.makeApiRequest({
            endpoint: "unlinkCategoryDefault",
            method: "POST",
            data: {
                categoryIdFromPOS,
                retailBrandID: selectedRetailBrand?.value
            },
            setLoader: setIsSaving
        });

        if (res?.data?.status !== 200) return false;

        Library.showToastMessage({
            type: "success",
            title: "Size system unlinked successfully"
        });

        onRefreshAction();
        return true;
    };

    const isUnlinkEnabled = item => {
        const result = item?.sizeSystemName && item?.categoryRecordID;
        return result;
    };
    // #endregion

    // #region COMPUTED VARS
    const isEditingCategory = !!editingCategoryDefault;
    const currentCategorySnapshot = getCurrentCategorySnapshot();
    const editHasChanges =
        isEditingCategory &&
        categoryEditSnapshot &&
        !categorySnapshotsAreEqual(categoryEditSnapshot, currentCategorySnapshot);
    const categoryFormLocked = !isEditingCategory;
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setupHeaders({
            props,
            searchValue,
            onSearch,
            onRefreshAction,
            allRetailBrandsList,
            onRetailBrandChange,
            selectedRetailBrand,
            allPOSList,
            onSelectedPOSChanged,
            onFiltersLoaded
        });
    }, [allRetailBrandsList, selectedRetailBrand, allPOSList]);

    useEffect(() => {
        getProductSizeGroupsFromPOSRequest();
    }, [selectedRetailBrand]);

    useEffect(() => {
        setEditSaveButtonEnabled(isEditingCategory && categoryFormIsValid() && !!editHasChanges);
        setHasUnsavedChanges(isEditingCategory && !!editHasChanges);
    }, [parentCategoryName, subCategoryName, selectedSizeSystem, editingCategoryDefault, categoryEditSnapshot]);
    // #endregion

    // #region RENDER
    return (
        <StyledPage className="my-defaults-categories-page">
            <FullScreenOverlay show={isSaving}>
                <DialogBoxComponent
                    headingColor="var(--text-icons-green-500)"
                    heading={isEditingCategory ? "UPDATING..." : "SAVING..."}
                    showCancel={false}
                    showConfirm={false}
                    loading={isSaving}
                />
            </FullScreenOverlay>

            <FormContainer title="DEFAULT CATEGORY DETAILS" showDefaultRightLegends={true}>
                <div className="add-category-row">
                    <div className="add-category-parent-name">
                        <FormField
                            type="text"
                            title="Parent category name"
                            placeholder="Insert category name"
                            requiredType="disabledSimple"
                            varName="parentCategoryName"
                            tooltip="Parent category name"
                            value={parentCategoryName}
                            disabled={true}
                        />
                    </div>

                    <div className="add-category-sub-name">
                        <FormField
                            type="text"
                            title="Sub-category name"
                            placeholder="Insert sub-category name"
                            requiredType="disabledSimple"
                            varName="subCategoryName"
                            tooltip="Sub-category name"
                            value={subCategoryName}
                            disabled={true}
                        />
                    </div>

                    <div className="add-category-size-system">
                        <FormField
                            type="dropdown"
                            title="Size system"
                            placeholder="Select related size system"
                            requiredType={categoryFormLocked ? "locked" : "mandatory"}
                            varName="sizeSystem"
                            tooltip="Size system"
                            dropdownData={sizeSystemDropdownData}
                            disabled={categoryFormLocked}
                            onDropdownChange={onSizeSystemChange}
                            isDropdownSearchable={true}
                        />
                    </div>

                    <div className="add-category-variants">
                        <FormField
                            type="text"
                            title="Category variants"
                            placeholder="Insert variants' names + ENTER"
                            requiredType="disabledSimple"
                            varName="categoryNameDescription"
                            tooltip="Category variants"
                            disabled={true}
                        />
                    </div>

                    <BtnLargeSolid
                        className="add-category-button"
                        text={isEditingCategory ? "UPDATE" : "ADD"}
                        disabled={!editSaveButtonEnabled}
                        normalColor="var(--Buttons-All-Platforms-Gray-500)"
                        hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                        hoverBorderColor="var(--text-icons-orange-500)"
                        disabledColor="var(--buttons-all-platforms-gray-500, #10141b)"
                        textStyle={{
                            fontSize: "11px",
                            textTransform: "uppercase"
                        }}
                        onClick={saveCategoryEditClicked}
                    />
                </div>
            </FormContainer>

            <ContainerLoader
                isLoading={!isLoadingApp && listComponentIsLoading}
                className="w-100"
                isLoadingOverlay={true}
                addBlurFilters={false}>
                <ListComponent
                    className="w-100"
                    tableClassName="expandedRowsCentered"
                    title="DEFAULT CATEGORIES - AS IN POS"
                    columns={listColumns}
                    api={{
                        endpoint: "getCategoryDefaults",
                        formData: {
                            retailBrandID: selectedRetailBrand?.value || "",
                            limit: listNumRows,
                            ...filters
                        },
                        onSuccess: mapListData
                    }}
                    selectableRows={true}
                    showLoader={false}
                    onListLoading={loading => setListComponentIsLoading(loading)}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    requestEnabled={requestsEnabled}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                    actions={{
                        includeActions: true,
                        editIsVisible: true,
                        onClickEdit: onClickEditHandler,
                        editIsDisabled: row => !valueIsPresent(row?.categoryRecordID),
                        removeIsVisible: true,
                        suspendIsVisible: false,
                        actionColumnWidth: "100px",
                        actionColumnTitle: "Actions",
                        //extraActionsShowDivider: false,
                        extraActions: [
                            {
                                title: "Unlink",
                                onClick: item => onUnlinkClick(item),
                                isDisabled: item => !isUnlinkEnabled(item)
                                //icon: <span className="fa fa-light fa-chain-broken"></span>
                            }
                        ]
                    }}
                />
            </ContainerLoader>
        </StyledPage>
    );
    // #endregion
}

// #region HEADERS
function setupHeaders({
    props,
    searchValue,
    onSearch,
    onRefreshAction,
    allRetailBrandsList,
    onRetailBrandChange,
    selectedRetailBrand,
    allPOSList,
    onSelectedPOSChanged,
    onFiltersLoaded
}) {
    props.setControlBarLeftBtns([
        {
            customSearch: (
                <CustomSearch
                    enabled={false}
                    placeholder="Search"
                    onTextChange={onSearch}
                    searchValue={searchValue}
                    onClearSearch={() => onSearch("")}
                />
            )
        }
    ]);

    props.setControlBarCenterBtns([
        { to: "/products", active: false, disabled: false, title: "LIVE" },
        { to: "/products/my-defaults/seasons", active: true, disabled: false, title: "MY DEFAULTS" }
    ]);

    props.setControlBarRightBtns([
        {
            to: "/products/my-defaults/seasons",
            active: false,
            disabled: false,
            title: "Seasons",
            icon: "snowflake-02"
        },
        {
            to: "/products/my-defaults/genders",
            active: false,
            disabled: false,
            title: "Genders",
            icon: "link-05"
        },
        {
            to: "/products/my-defaults/colors",
            active: false,
            disabled: false,
            title: "Colors",
            icon: "palette"
        },
        {
            to: "/products/my-defaults/categories",
            active: true,
            disabled: false,
            title: "Categ",
            icon: "grid-01"
        },
        {
            to: "/products/my-defaults/sizes",
            active: false,
            disabled: false,
            title: "Sizes",
            icon: "ruler"
        }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    useLayoutButton: {
                        useInsteadOfGridList: true,
                        text: "EXPORT",
                        enabled: false,
                        onClick: undefined
                    },
                    gridEnabled: false,
                    listEnabled: false
                }}
                search={{
                    visible: false
                }}
                onRefreshClick={onRefreshAction}
                firstOrganize={{
                    visible: true,
                    dropdownData: allRetailBrandsList,
                    onDropdownChange: onRetailBrandChange,
                    disabled: false,
                    type: "singleSelect",
                    selectedValue: selectedRetailBrand?.name
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allPOSList,
                    onDropdownChange: onSelectedPOSChanged,
                    disabled: false,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all POS",
                        labelAllSelected: "All POS",
                        labelNoneSelected: "--",
                        allItems: allPOSList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        usePOSSystems: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                settingsButton={{
                    visible: true,
                    enabled: false
                }}
            />
        ),
        visible: false,
        gridEnabled: false,
        listEnabled: false,
        onRefreshAction
    });
}
// #endregion

// #region SMALL COMPONENTS

// #endregion
